import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { 
  Home, 
  CreditCard, 
  Send, 
  FileText, 
  MessageCircle,
  Eye,
  EyeOff,
  ArrowUpRight,
  ArrowDownLeft,
  Calculator,
  LogOut,
  Sparkles
} from 'lucide-react';
import { mockUser, mockTransactions, mockLoans } from '../mock';
import { toast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import AgroIAChat from './AgroIAChat';

const BankingDashboard = () => {
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferAccount, setTransferAccount] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [loanTerm, setLoanTerm] = useState('12');
  const [calculatedLoan, setCalculatedLoan] = useState(null);

  const handleTransfer = (e) => {
    e.preventDefault();
    if (!transferAmount || !transferAccount) {
      toast({
        title: 'Error',
        description: 'Por favor completa todos los campos',
        variant: 'destructive'
      });
      return;
    }
    toast({
      title: 'Transferencia exitosa',
      description: `Bs. ${parseFloat(transferAmount).toLocaleString('es-VE', { minimumFractionDigits: 2 })} transferidos a cuenta ${transferAccount}`,
    });
    setTransferAmount('');
    setTransferAccount('');
  };

  const calculateLoan = () => {
    if (!loanAmount) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa el monto del crédito',
        variant: 'destructive'
      });
      return;
    }
    
    const amount = parseFloat(loanAmount);
    const months = parseInt(loanTerm);
    const interestRate = 0.085; // 8.5%
    const monthlyRate = interestRate / 12;
    const monthlyPayment = (amount * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const totalPayment = monthlyPayment * months;
    const totalInterest = totalPayment - amount;
    
    setCalculatedLoan({
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      term: months
    });
  };

  const formatCurrency = (amount) => {
    return `Bs. ${parseFloat(amount).toLocaleString('es-VE', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100/30">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b border-green-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_5732c38f-46bd-4a56-9931-c3cf86a70f4f/artifacts/nfkada7y_logo.jpg" alt="Banco Agrícola" className="h-14 w-auto" />
              <div>
                <span className="font-semibold text-gray-900 block text-lg">Online Banking</span>
                <span className="text-sm text-green-600 font-medium">Sembrando Soberanía</span>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-gray-600">Bienvenido</div>
                <div className="font-semibold text-gray-900">{mockUser.name}</div>
              </div>
              <Link to="/">
                <Button variant="outline" size="icon">
                  <LogOut className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4">
                <nav className="space-y-2">
                  <Button 
                    variant={activeTab === 'dashboard' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'dashboard' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Inicio
                  </Button>
                  <Button 
                    variant={activeTab === 'transfer' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'transfer' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('transfer')}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Transferir
                  </Button>
                  <Button 
                    variant={activeTab === 'loans' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'loans' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('loans')}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Mis Créditos
                  </Button>
                  <Button 
                    variant={activeTab === 'simulator' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'simulator' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('simulator')}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Simulador
                  </Button>
                  <Button 
                    variant={activeTab === 'transactions' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'transactions' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('transactions')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Movimientos
                  </Button>
                  <Button 
                    variant={activeTab === 'chat' ? 'default' : 'ghost'} 
                    className={`w-full justify-start ${activeTab === 'chat' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setActiveTab('chat')}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    AgroIA
                  </Button>
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <>
                {/* Balance Card */}
                <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardDescription className="text-green-100">Saldo Disponible</CardDescription>
                        <CardTitle className="text-4xl font-bold mt-2">
                          {showBalance ? formatCurrency(mockUser.balance) : '••••••'}
                        </CardTitle>
                        <p className="text-sm text-green-100 mt-2">{mockUser.accountType}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setShowBalance(!showBalance)}
                        className="text-white hover:bg-white/20"
                      >
                        {showBalance ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm text-green-100">Número de cuenta</p>
                      <p className="font-mono text-lg">{mockUser.accountNumber}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('transfer')}>
                    <CardHeader className="pb-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                        <Send className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Transferir</CardTitle>
                      <CardDescription>Envía dinero rápidamente</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('simulator')}>
                    <CardHeader className="pb-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                        <Calculator className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">Simular Crédito</CardTitle>
                      <CardDescription>Calcula tu préstamo</CardDescription>
                    </CardHeader>
                  </Card>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('chat')}>
                    <CardHeader className="pb-3">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                        <Sparkles className="w-6 h-6 text-green-600" />
                      </div>
                      <CardTitle className="text-lg">AgroIA</CardTitle>
                      <CardDescription>Ayuda instantánea</CardDescription>
                    </CardHeader>
                  </Card>
                </div>

                {/* Recent Transactions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Movimientos Recientes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {mockTransactions.slice(0, 3).map((transaction) => (
                        <div key={transaction.id} className="flex items-center justify-between py-3 border-b last:border-0">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              transaction.type === 'ingreso' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {transaction.type === 'ingreso' ? 
                                <ArrowDownLeft className="w-5 h-5 text-green-600" /> : 
                                <ArrowUpRight className="w-5 h-5 text-red-600" />
                              }
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{transaction.description}</p>
                              <p className="text-sm text-gray-500">{transaction.date}</p>
                            </div>
                          </div>
                          <div className={`font-semibold ${
                            transaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'ingreso' ? '+' : ''}
                            {formatCurrency(Math.abs(transaction.amount))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-4" onClick={() => setActiveTab('transactions')}>
                      Ver todos los movimientos
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Transfer Tab */}
            {activeTab === 'transfer' && (
              <Card>
                <CardHeader>
                  <CardTitle>Realizar Transferencia</CardTitle>
                  <CardDescription>Envía dinero a otras cuentas de forma segura</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleTransfer} className="space-y-4">
                    <div>
                      <Label htmlFor="account">Cuenta destino</Label>
                      <Input 
                        id="account" 
                        placeholder="0172-0234-56-7890123456" 
                        value={transferAccount}
                        onChange={(e) => setTransferAccount(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="amount">Monto (Bs.)</Label>
                      <Input 
                        id="amount" 
                        type="number" 
                        placeholder="0.00" 
                        value={transferAmount}
                        onChange={(e) => setTransferAmount(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="concept">Concepto</Label>
                      <Input id="concept" placeholder="Pago de insumos agrícolas" />
                    </div>
                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                      <Send className="w-4 h-4 mr-2" />
                      Transferir
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Loans Tab */}
            {activeTab === 'loans' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Mis Créditos Activos</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {mockLoans.map((loan) => (
                      <Card key={loan.id} className="border-green-100">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg">{loan.type}</CardTitle>
                              <CardDescription>Próximo pago: {loan.nextPaymentDate}</CardDescription>
                            </div>
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{loan.status}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm text-gray-500">Monto Original</p>
                              <p className="text-lg font-semibold">{formatCurrency(loan.amount)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Saldo Actual</p>
                              <p className="text-lg font-semibold">{formatCurrency(loan.balance)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Tasa</p>
                              <p className="text-lg font-semibold">{loan.interestRate}%</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Cuota Mensual</p>
                              <p className="text-lg font-semibold">{formatCurrency(loan.monthlyPayment)}</p>
                            </div>
                          </div>
                          <Button className="w-full mt-4 bg-green-600 hover:bg-green-700">Pagar Cuota</Button>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Simulator Tab */}
            {activeTab === 'simulator' && (
              <Card>
                <CardHeader>
                  <CardTitle>Simulador de Crédito Agrícola</CardTitle>
                  <CardDescription>Calcula tu cuota mensual y planifica tu inversión</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="loanAmount">Monto del Crédito (Bs.)</Label>
                      <Input 
                        id="loanAmount" 
                        type="number" 
                        placeholder="1935000" 
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="loanTerm">Plazo</Label>
                      <Select value={loanTerm} onValueChange={setLoanTerm}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12 meses</SelectItem>
                          <SelectItem value="24">24 meses</SelectItem>
                          <SelectItem value="36">36 meses</SelectItem>
                          <SelectItem value="48">48 meses</SelectItem>
                          <SelectItem value="60">60 meses</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={calculateLoan} className="w-full bg-green-600 hover:bg-green-700">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calcular
                    </Button>
                  </div>

                  {calculatedLoan && (
                    <Card className="bg-green-50 border-green-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Resultado de la Simulación</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cuota Mensual:</span>
                          <span className="font-bold text-xl text-green-600">{formatCurrency(calculatedLoan.monthlyPayment)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total a Pagar:</span>
                          <span className="font-semibold">{formatCurrency(calculatedLoan.totalPayment)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Intereses:</span>
                          <span className="font-semibold">{formatCurrency(calculatedLoan.totalInterest)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Plazo:</span>
                          <span className="font-semibold">{calculatedLoan.term} meses</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tasa de Interés:</span>
                          <span className="font-semibold">8.5% anual</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <Card>
                <CardHeader>
                  <CardTitle>Historial de Movimientos</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-4">
                      {mockTransactions.map((transaction) => (
                        <div key={transaction.id} className="flex items-center justify-between py-4 border-b">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              transaction.type === 'ingreso' ? 'bg-green-100' : 'bg-red-100'
                            }`}>
                              {transaction.type === 'ingreso' ? 
                                <ArrowDownLeft className="w-6 h-6 text-green-600" /> : 
                                <ArrowUpRight className="w-6 h-6 text-red-600" />
                              }
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{transaction.description}</p>
                              <p className="text-sm text-gray-500">{transaction.date} • {transaction.category}</p>
                              <Badge variant="outline" className="mt-1">{transaction.status}</Badge>
                            </div>
                          </div>
                          <div className={`text-right`}>
                            <div className={`font-bold text-lg ${
                              transaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.type === 'ingreso' ? '+' : ''}
                              {formatCurrency(Math.abs(transaction.amount))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <AgroIAChat inline={true} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankingDashboard;